import './App.css';
import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import PrivateRoute from './utils/PrivateRoute';
import PluginGuard from './components/PluginGuard';
import { AuthProvider } from './context/AuthContext';
import { PluginProvider } from './context/PluginContext';
import { SidebarBadgesProvider } from './context/SidebarBadgesContext';
import { hasCatalogSchema, getDashboardWidgets, canUseTools, configVersion, subscribeBusinessConfig } from './utils/businessConfig';

// Light components — load eagerly (small, essential for first render)
import Login from './pages/Login';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Base from './components/Base';
import AdminRoute from './components/admin/AdminRoute';
import ImpersonationBanner from './components/ImpersonationBanner';

// Medium components — lazy but preloaded
// Signup is a full-page three-step wizard (details → business → modules); the
// old single-page Register form it replaces is kept in git history only.
const Signup = lazy(() => import('./pages/Signup'));
const Setup = lazy(() => import('./pages/Setup'));
const LandingPage = lazy(() => import('./pages/landing_page'));
const HomePage = lazy(() => import('./pages/HomePage'));
const ErpDashboard = lazy(() => import('./pages/Dashboard/ErpDashboard'));

// NOTE: We intentionally do NOT eagerly prefetch the TemplateThemes / VisualTemplateEditor
// chunks. TemplateThemes is a very large chunk; prefetching it for every visitor (even those
// who never open it) hurt initial load. These routes are React.lazy()-loaded on demand below,
// which is fast enough and avoids downloading a big chunk users may never need.

// Heavy components — load lazily (only when route is visited)
const WebsiteBuilderLanding = lazy(() => import('./pages/WebsiteBuilderLanding'));
const ContactUs = lazy(() => import('./pages/ContactUs'));
const TermsAndServices = lazy(() => import('./components/TermsNcondn'));
const ReturnPolicy = lazy(() => import('./components/ReturnPolicy'));
const PaymentShipping = lazy(() => import('./components/PaymentShipping'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const Faqs = lazy(() => import('./components/Faqs'));
const Uploadproduct = lazy(() => import('./pages/Uploadproduct'));
const SchemaCatalog = lazy(() => import('./pages/Catalog/SchemaCatalog'));

// Catalog entry point. Non-fashion businesses (those with a blueprint catalog
// schema) get the schema-driven form in their own words; fashion keeps its
// proven bespoke Uploadproduct. hasCatalogSchema() is false for every existing
// user, so nothing changes for them.
const CatalogEntry = () => (
  hasCatalogSchema() ? <SchemaCatalog/> : <Uploadproduct/>
);

// Home dashboard. Businesses whose blueprint declares dashboard widgets (pure
// ERP verticals) get the native ERP dashboard; fashion (no widgets) keeps its
// storefront HomePage. getDashboardWidgets() is empty for every existing user.
const HomeDashboard = () => {
  // Which home screen this business gets depends on config that loads AFTER the
  // first render. Subscribing alone loses the race when the config arrives
  // first, so we also compare versions on mount — otherwise a kirana shop sits
  // on the fashion homepage until it navigates somewhere else.
  const [version, setVersion] = React.useState(configVersion);
  React.useEffect(() => {
    if (configVersion() !== version) setVersion(configVersion());
    return subscribeBusinessConfig(setVersion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return getDashboardWidgets().length > 0 ? <ErpDashboard/> : <HomePage/>;
};
const Profile = lazy(() => import('./components/Profile'));
const Contact = lazy(() => import('./components/Contact'));
const AboutMe = lazy(() => import('./components/AboutMe'));
const Orders = lazy(() => import('./components/Orders'));
const Proposals = lazy(() => import('./components/Proposals'));
const EditProduct = lazy(() => import('./components/EditProduct'));
const TemplateSelector = lazy(() => import('./components/Subscription'));
const TemplateThemes = lazy(() => import('./pages/TemplateThemes'));
// Public, no-login full-page render of a base template — used to auto-screenshot
// gallery cover images and for visual QA. Route: /template-shot/:templateId
const TemplateShot = lazy(() => import('./pages/TemplateShot'));
const VisualTemplateEditor = lazy(() => import('./pages/VisualTemplateEditor'));
const DesignerOnboarding = lazy(() => import('./components/DesignerOnboarding'));
const AdminDesignerDashboard = lazy(() => import('./components/AdminDesignerDashboard'));
const DesignerProfile = lazy(() => import('./components/DesignerProfile'));
const MyCoupons = lazy(() => import('./pages/MyCoupons'));
const Analytics = lazy(() => import('./pages/Analytics'));
const SEOSettings = lazy(() => import('./pages/SEOSettings'));
const CollectionsManager = lazy(() => import('./pages/CollectionsManager'));
const CategoriesPage = lazy(() => import('./pages/Categories/CategoriesPage'));
const OrderBook = lazy(() => import('./components/orderbook/OrderBook'));
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const PageDashboard = lazy(() => import('./components/TemplateBuilder/PageDashboard'));
// L7: Version history browseable UI for template drafts
const VersionHistory = lazy(() => import('./pages/VersionHistory'));

// ─── Daily-Ops SaaS features (phases §1–§7) ────────────────────────────
// §1 Morning Dashboard
const Dashboard = lazy(() => import('./pages/Dashboard'));
// §4 Inventory
const InventoryIndex = lazy(() => import('./pages/Inventory'));
const CounterBilling = lazy(() => import('./pages/CounterBilling'));
const Khata = lazy(() => import('./pages/Khata'));
const DayBook = lazy(() => import('./pages/DayBook'));
const Books = lazy(() => import('./pages/Books'));
const Batches = lazy(() => import('./pages/Batches'));
const B2BBuyers = lazy(() => import('./pages/B2BBuyers'));
const Team = lazy(() => import('./pages/Team'));
const BOM = lazy(() => import('./pages/BOM'));
const Serials = lazy(() => import('./pages/Serials'));
const Labels = lazy(() => import('./pages/Labels'));
const PriceLists = lazy(() => import('./pages/PriceLists'));
const ItemRecipes = lazy(() => import('./pages/ItemRecipes'));
const Reminders = lazy(() => import('./pages/Reminders'));
const Loyalty = lazy(() => import('./pages/Loyalty'));
const Vouchers = lazy(() => import('./pages/Vouchers'));
const Campaigns = lazy(() => import('./pages/Campaigns'));
const BankRec = lazy(() => import('./pages/BankRec'));
const Documents = lazy(() => import('./pages/Documents'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const Approvals = lazy(() => import('./pages/Approvals'));
const Quotes = lazy(() => import('./pages/Quotes'));
const Leads = lazy(() => import('./pages/Leads'));
// COMMERCE_DEPTH_PLAN P1 — billing paper-trail
const Estimates = lazy(() => import('./pages/Estimates'));
const CreditNotes = lazy(() => import('./pages/CreditNotes'));
const Challans = lazy(() => import('./pages/Challans'));
const Compliance = lazy(() => import('./pages/Compliance'));
const Locations = lazy(() => import('./pages/Locations'));
const StockTake = lazy(() => import('./pages/StockTake'));
const OrderBoard = lazy(() => import('./pages/OrderBoard'));
const PreOrders = lazy(() => import('./pages/PreOrders'));
const Appointments = lazy(() => import('./pages/Appointments'));
const VariantManager = lazy(() => import('./pages/Inventory/VariantManager'));
const StockHistory = lazy(() => import('./pages/Inventory/StockHistory'));
// §5 Customer CRM
const CustomersList = lazy(() => import('./pages/Customers'));
const CustomerDetail = lazy(() => import('./pages/Customers/CustomerDetail'));
const SegmentBuilder = lazy(() => import('./pages/Customers/SegmentBuilder'));
// §2 Unified Orders
const UnifiedInbox = lazy(() => import('./pages/Orders/UnifiedInbox'));
// §3 Production
const ProductionBoard = lazy(() => import('./pages/Production/ProductionBoard'));
const KarigarList = lazy(() => import('./pages/Production/KarigarList'));
const KarigarDetail = lazy(() => import('./pages/Production/KarigarDetail'));
// §6 Billing & Payments
const PaymentsDashboard = lazy(() => import('./pages/Payments/Dashboard'));
// Connect Payments — designer links their OWN Razorpay account (replaces KYC/Route onboarding)
const ConnectPayments = lazy(() => import('./pages/Payments/ConnectPayments'));
// Earnings & Payouts — live data pulled from the designer's OWN Razorpay account
const Earnings = lazy(() => import('./pages/Payments/Earnings'));
// Store Settings — shipping/returns/COD/welcome-coupon the storefront reads
const StoreSettings = lazy(() => import('./pages/Payments/StoreSettings'));
// Catalog Settings — categories & sizes, fabric care, upload defaults
const CatalogSettings = lazy(() => import('./pages/Catalog/CatalogSettings'));
// Per-product add-ons editor
const ProductAddonsEditor = lazy(() => import('./pages/Catalog/ProductAddonsEditor'));
const ExpenseList = lazy(() => import('./pages/Expenses/ExpenseList'));
const SupplierLedger = lazy(() => import('./pages/Expenses/SupplierLedger'));
// Purchases / Stock-In — supplier purchase accounting (adds stock + tracks payables)
const PurchasesPage = lazy(() => import('./pages/Purchases/PurchasesPage'));
// §7 Reports
const ReportsPnL = lazy(() => import('./pages/Reports/PnL'));
const ReportsGSTR1 = lazy(() => import('./pages/Reports/GSTR1'));
const ReportsComparisons = lazy(() => import('./pages/Reports/Comparisons'));
const ReportsGoals = lazy(() => import('./pages/Reports/GoalTracker'));
const ReportsStockAgeing = lazy(() => import('./pages/Reports/StockAgeing'));
const ReportsCommerce = lazy(() => import('./pages/Reports/CommerceReports'));
// Automations / AI rules engine
const Automations = lazy(() => import('./pages/Automations'));
// WhatsApp — setup form + messages inbox, unified
const WhatsAppHub = lazy(() => import('./pages/WhatsApp'));
// Plugin Store — opt-in business features
const PluginStore = lazy(() => import('./pages/PluginStore'));
const Tools = lazy(() => import('./pages/Tools'));
const Personalize = lazy(() => import('./pages/Personalize'));
// Email Marketing — broadcasts + lifecycle emails
const EmailMarketing = lazy(() => import('./pages/EmailMarketing'));

// [LOW-9] Real 404 page. Previously the catch-all `*` redirected to
// /homepage (which, when unauthenticated, bounced to /login) — silently
// masking broken links and typo'd URLs. Render an explicit Not Found page
// instead so unknown routes are visible rather than disguised as a redirect.
function NotFound() {
  return (
    <div className="flex flex-col items-center justify-center h-screen text-center px-4">
      <h1 className="text-6xl font-extrabold text-[#B40A0A]">404</h1>
      <p className="mt-4 text-lg font-semibold text-gray-900">Page not found</p>
      <p className="mt-2 text-sm text-gray-500">
        The page you’re looking for doesn’t exist or may have moved.
      </p>
      <div className="mt-6 flex gap-3">
        <Link
          to="/homepage"
          className="px-4 py-2 rounded-lg text-white font-semibold"
          style={{ background: '#B40A0A' }}
        >
          Go to Dashboard
        </Link>
        <Link
          to="/"
          className="px-4 py-2 rounded-lg font-semibold text-gray-700 border border-gray-300 hover:bg-gray-50"
        >
          Back Home
        </Link>
      </div>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
    <Router>
      <AuthProvider>
        <PluginProvider>
        <SidebarBadgesProvider>
        {/* Route-level boundary: a render error in any lazy route is isolated
            here so the app shell (Router + providers) survives instead of the
            single root boundary blanking the whole app. */}
        <ErrorBoundary>
        <ImpersonationBanner />
        <Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div></div>}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/old-landing" element={<LandingPage />} />
          <Route path="/contactus" element={<ContactUs />} />
          <Route path="/login" element={<Login />} />
          <Route path="/termsNconditions" element={<TermsAndServices/>} />
          <Route path="/payShip" element={<PaymentShipping/>} />
          <Route path="/rtnPolicy" element={<ReturnPolicy/>} />
          <Route path="/privacyPolicy" element={<PrivacyPolicy/>} />
          <Route path="/Faqs" element={<Faqs/>} />
          <Route path="/forgot-password" element={<ForgotPassword/>} />
          <Route path="/reset-password/:token" element={<ResetPassword/>} />
          <Route path="/template-shot/:templateId" element={<TemplateShot />} />
          <Route
            path="/homepage"
            element={
              <PrivateRoute>
                <HomeDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/UploadProduct"
            element={
              <PrivateRoute>
                <CatalogEntry/>
              </PrivateRoute>
            }
          />
          <Route
            path="/EditProduct/:productId"
            element={
              <PrivateRoute>
                <EditProduct/>
              </PrivateRoute>
            }
          />
          <Route
            path="/Base"
            element={
              <PrivateRoute>
                <Base/>
              </PrivateRoute>
            }
          />
          <Route
            path="/Profile"
            element={
              <PrivateRoute>
                <Profile/>
              </PrivateRoute>
            }
          />
          <Route
            path="/my-coupons"
            element={
              <PrivateRoute>
                <MyCoupons/>
              </PrivateRoute>
            }
          />
          <Route
            path="/collections-manager"
            element={
              <PrivateRoute>
                <CollectionsManager/>
              </PrivateRoute>
            }
          />
          <Route
            path="/categories"
            element={
              <PrivateRoute>
                <CategoriesPage/>
              </PrivateRoute>
            }
          />
          <Route
            path="/TemplateThemes"
            element={
              <PrivateRoute>
                <TemplateThemes/>
              </PrivateRoute>
            }
          />
          <Route
            path="/visual-editor"
            element={
              <PrivateRoute>
                <VisualTemplateEditor/>
              </PrivateRoute>
            }
          />
          <Route
            path="/template/:templateId/versions"
            element={
              <PrivateRoute>
                <VersionHistory/>
              </PrivateRoute>
            }
          />
          <Route
            path="/template-builder/:templateId"
            element={
              <PrivateRoute>
                <PageDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/Contact"
            element={
              <PrivateRoute>
                <Contact/>
              </PrivateRoute>
            }
          />
          <Route
            path="/AboutMe"
            element={
              <PrivateRoute>
                <AboutMe/>
              </PrivateRoute>
            }
          />
          <Route
            path="/Orders"
            element={
              <PrivateRoute>
                <Orders/>
              </PrivateRoute>
            }
          />
          <Route
            path="/orderbook"
            element={
              <PrivateRoute>
                <OrderBook/>
              </PrivateRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <PrivateRoute>
                <Analytics/>
              </PrivateRoute>
            }
          />
          <Route
            path="/seo-settings"
            element={
              <PrivateRoute>
                <SEOSettings/>
              </PrivateRoute>
            }
          />
          <Route
            path="/Proposals"
            element={
              <PrivateRoute>
                <PluginGuard pluginKey="bid_requests">
                  <Proposals/>
                </PluginGuard>
              </PrivateRoute>
            }
          />
          <Route
            path="/subscription"
            element={
              <PrivateRoute>
                <TemplateSelector />
              </PrivateRoute>
            }
          />
          <Route
            path="/inikru-admin"
            element={
              <AdminRoute>
                <AdminDashboard/>
              </AdminRoute>
            }
          />
          <Route
            path="/designer-onboarding"
            element={
              <PrivateRoute>
                <DesignerOnboarding/>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin-designers"
            element={
              <AdminRoute>
                <AdminDesignerDashboard/>
              </AdminRoute>
            }
          />
          <Route
            path="/designer-profile"
            element={
              <PrivateRoute>
                <DesignerProfile/>
              </PrivateRoute>
            }
          />
          <Route path="/Register" element={<Signup/>}/>

          {/* ─── Plugin-gated feature routes — one plugin per sidebar item ─── */}
          <Route path="/dashboard" element={<PrivateRoute><PluginGuard pluginKey="daily_ops"><Dashboard/></PluginGuard></PrivateRoute>} />
          <Route path="/inventory" element={<PrivateRoute><PluginGuard pluginKey="inventory"><InventoryIndex/></PluginGuard></PrivateRoute>} />
          {/* A3 — Counter Billing (POS). Capability-gated inside the page. */}
          <Route path="/counter" element={<PrivateRoute><CounterBilling/></PrivateRoute>} />
          {/* A4 — Customer Khata (udhaar ledger). Capability-gated inside the page. */}
          <Route path="/khata" element={<PrivateRoute><Khata/></PrivateRoute>} />
          {/* A5 — Day Book + CA Pack. Capability-gated inside the page. */}
          <Route path="/daybook" element={<PrivateRoute><DayBook/></PrivateRoute>} />
          {/* Books — double-entry statements for the CA. Capability-gated inside the page. */}
          <Route path="/books" element={<PrivateRoute><Books/></PrivateRoute>} />
          {/* A6 — Batches & Expiry. Capability-gated inside the page. */}
          <Route path="/batches" element={<PrivateRoute><Batches/></PrivateRoute>} />
          {/* A7 — B2B buyers. Capability-gated inside the page. */}
          <Route path="/b2b-buyers" element={<PrivateRoute><B2BBuyers/></PrivateRoute>} />
          {/* A8 — Recipes & yield (BOM). Capability-gated inside the page. */}
          <Route path="/recipes" element={<PrivateRoute><BOM/></PrivateRoute>} />
          {/* Serial / IMEI tracking. Capability-gated inside the page. */}
          <Route path="/serials" element={<PrivateRoute><Serials/></PrivateRoute>} />
          {/* P2.1 — Barcode labels. Capability-gated inside the page. */}
          <Route path="/labels" element={<PrivateRoute><Labels/></PrivateRoute>} />
          {/* P2.3 — Price lists. Capability-gated inside the page. */}
          <Route path="/price-lists" element={<PrivateRoute><PriceLists/></PrivateRoute>} />
          {/* P2.4 — Item recipes (café BOM). Capability-gated inside the page. */}
          <Route path="/item-recipes" element={<PrivateRoute><ItemRecipes/></PrivateRoute>} />
          {/* P3.1 — Payment reminders. Capability-gated inside the page. */}
          <Route path="/reminders" element={<PrivateRoute><Reminders/></PrivateRoute>} />
          {/* P3.2 — Loyalty points. Capability-gated inside the page. */}
          <Route path="/loyalty" element={<PrivateRoute><Loyalty/></PrivateRoute>} />
          {/* P3.3 — Gift vouchers. Capability-gated inside the page. */}
          <Route path="/vouchers" element={<PrivateRoute><Vouchers/></PrivateRoute>} />
          {/* P3.4 — WhatsApp campaigns. Capability-gated inside the page. */}
          <Route path="/campaigns" element={<PrivateRoute><Campaigns/></PrivateRoute>} />
          {/* Bank Reconciliation — match ledger to bank statement. Capability-gated inside. */}
          <Route path="/bank-reconciliation" element={<PrivateRoute><BankRec/></PrivateRoute>} />
          {/* Document Locker — owner/manager only (gated inside the page). */}
          <Route path="/documents" element={<PrivateRoute><Documents/></PrivateRoute>} />
          {/* Portfolio / public link-hub management. */}
          <Route path="/portfolio" element={<PrivateRoute><Portfolio/></PrivateRoute>} />
          {/* Wave 2 — approvals + B2B quotations + leads + compliance. Capability-gated inside. */}
          <Route path="/approvals" element={<PrivateRoute><Approvals/></PrivateRoute>} />
          <Route path="/quotes" element={<PrivateRoute><Quotes/></PrivateRoute>} />
          <Route path="/leads" element={<PrivateRoute><Leads/></PrivateRoute>} />
          {/* COMMERCE_DEPTH_PLAN P1 — billing paper-trail. Tool-gated inside each page. */}
          <Route path="/estimates" element={<PrivateRoute><Estimates/></PrivateRoute>} />
          <Route path="/credit-notes" element={<PrivateRoute><CreditNotes/></PrivateRoute>} />
          <Route path="/challans" element={<PrivateRoute><Challans/></PrivateRoute>} />
          <Route path="/compliance" element={<PrivateRoute><Compliance/></PrivateRoute>} />
          <Route path="/locations" element={<PrivateRoute><Locations/></PrivateRoute>} />
          <Route path="/stock-take" element={<PrivateRoute><StockTake/></PrivateRoute>} />
          <Route path="/order-board" element={<PrivateRoute><OrderBoard/></PrivateRoute>} />
          <Route path="/preorders" element={<PrivateRoute><PreOrders/></PrivateRoute>} />
          <Route path="/appointments" element={<PrivateRoute><Appointments/></PrivateRoute>} />
          <Route path="/inventory/product/:productId" element={<PrivateRoute><PluginGuard pluginKey="inventory"><VariantManager/></PluginGuard></PrivateRoute>} />
          <Route path="/inventory/variants/:variantId/history" element={<PrivateRoute><PluginGuard pluginKey="inventory"><StockHistory/></PluginGuard></PrivateRoute>} />
          <Route path="/customers" element={<PrivateRoute><PluginGuard pluginKey="customers"><CustomersList/></PluginGuard></PrivateRoute>} />
          <Route path="/customers/:id" element={<PrivateRoute><PluginGuard pluginKey="customers"><CustomerDetail/></PluginGuard></PrivateRoute>} />
          <Route path="/customer-segments" element={<PrivateRoute><PluginGuard pluginKey="customers"><SegmentBuilder/></PluginGuard></PrivateRoute>} />
          <Route path="/orders-unified" element={<PrivateRoute><PluginGuard pluginKey="unified_inbox"><UnifiedInbox/></PluginGuard></PrivateRoute>} />
          <Route path="/production" element={<PrivateRoute><PluginGuard pluginKey="production_board"><ProductionBoard/></PluginGuard></PrivateRoute>} />
          <Route path="/karigars" element={<PrivateRoute><PluginGuard pluginKey="team"><KarigarList/></PluginGuard></PrivateRoute>} />
          <Route path="/karigars/:id" element={<PrivateRoute><PluginGuard pluginKey="team"><KarigarDetail/></PluginGuard></PrivateRoute>} />
          <Route path="/payments" element={<PrivateRoute><PluginGuard pluginKey="payments"><PaymentsDashboard/></PluginGuard></PrivateRoute>} />
          <Route path="/connect-payments" element={<PrivateRoute><ConnectPayments/></PrivateRoute>} />
          {/* Team & Roles — owner-only staff logins. Gated inside the page via isOwner(). */}
          <Route path="/team" element={<PrivateRoute><Team/></PrivateRoute>} />
          <Route path="/earnings" element={<PrivateRoute><Earnings/></PrivateRoute>} />
          <Route path="/store-settings" element={<PrivateRoute><StoreSettings/></PrivateRoute>} />
          <Route path="/catalog-settings" element={<PrivateRoute><CatalogSettings/></PrivateRoute>} />
          <Route path="/product-addons/:productId" element={<PrivateRoute><ProductAddonsEditor/></PrivateRoute>} />
          <Route path="/expenses" element={<PrivateRoute><PluginGuard pluginKey="expenses"><ExpenseList/></PluginGuard></PrivateRoute>} />
          <Route path="/suppliers" element={<PrivateRoute><PluginGuard pluginKey="suppliers"><SupplierLedger/></PluginGuard></PrivateRoute>} />
          <Route path="/suppliers/:id" element={<PrivateRoute><PluginGuard pluginKey="suppliers"><SupplierLedger/></PluginGuard></PrivateRoute>} />
          <Route path="/purchases" element={<PrivateRoute><PurchasesPage/></PrivateRoute>} />
          <Route path="/reports/pnl" element={<PrivateRoute><PluginGuard pluginKey="report_pnl"><ReportsPnL/></PluginGuard></PrivateRoute>} />
          <Route path="/reports/gstr1" element={<PrivateRoute><PluginGuard pluginKey="report_gstr1"><ReportsGSTR1/></PluginGuard></PrivateRoute>} />
          <Route path="/reports/comparisons" element={<PrivateRoute><ReportsComparisons/></PrivateRoute>} />
          <Route path="/reports/goals" element={<PrivateRoute><PluginGuard pluginKey="report_goals"><ReportsGoals/></PluginGuard></PrivateRoute>} />
          {/* P2.5 — Stock ageing. Gated server-side by manages_stock. */}
          <Route path="/reports/stock-ageing" element={<PrivateRoute><ReportsStockAgeing/></PrivateRoute>} />
          {/* P4.2 — Commerce reports. Gated server-side by any commerce tool. */}
          <Route path="/reports/commerce" element={<PrivateRoute><ReportsCommerce/></PrivateRoute>} />
          <Route path="/whatsapp" element={<PrivateRoute><PluginGuard pluginKey="whatsapp"><WhatsAppHub/></PluginGuard></PrivateRoute>} />
          <Route path="/automations" element={<PrivateRoute><PluginGuard pluginKey="automations"><Automations/></PluginGuard></PrivateRoute>} />
          <Route path="/email-marketing" element={<PrivateRoute><PluginGuard pluginKey="email_marketing"><EmailMarketing/></PluginGuard></PrivateRoute>} />

          {/* Plugin Store — always available */}
          {/* Tools + Personalize are beta-gated (canUseTools). While the flag is
              off they're hidden in the nav AND route-blocked here, so a bookmarked
              or stale link bounces to the dashboard instead of reaching them. */}
          <Route path="/tools" element={<PrivateRoute>{canUseTools() ? <Tools/> : <Navigate to="/homepage" replace/>}</PrivateRoute>} />
          <Route path="/personalize" element={<PrivateRoute>{canUseTools() ? <Personalize/> : <Navigate to="/homepage" replace/>}</PrivateRoute>} />
          {/* The Plugin Store is superseded by Tools (which knows about every
              switch, not just the plugin half). Old links keep working. */}
          <Route path="/plugins" element={<Navigate to="/tools" replace />} />
          <Route path="/setup" element={<PrivateRoute><Setup/></PrivateRoute>} />

          {/* [LOW-9] Real 404 instead of a silent redirect to /homepage. */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        </ErrorBoundary>
        </SidebarBadgesProvider>
        </PluginProvider>
      </AuthProvider>
    </Router>
    </ErrorBoundary>
  );
}

export default App;